/*

Provide a set of Links formatted as a horizontal tab set

className: optionally override the className of the root <nav> element. Defaults to "tabs"
activeTabClassName: optionally override the activeClassName applied to the <a> tag of the active tab. Defaults to "tab-selected"
tabs: mandatory array of objects that are used to create the individual tabs
  href: mandatory link value. Passed to <Link> as the to property
  text: mandatory text for the tab's <Link>
  className: optional className to add to the tab. Gets applied to the <a>. Defaults to nothing.
  isIndex: optional boolean to say if the link is an index page for other tabs. Passed to <Link> as the onlyActiveOnIndex property
  isExternalLink: optiona boolean to indicate if link should open in a new tab. Defaults to false

*/
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router';
import CaretIcon from 'modules/Common/components/svg/CaretIcon';
import {globalCssExports, preventLinkScroll} from 'utils/commonUtils';
import {activeClassName} from 'utils/routeUtils';
import {isEqual} from 'lodash';

const MOVE_MINIMUM = 5;

const {arrowWidth} = globalCssExports("tabs");

@withRouter
export default class Tabs extends Component {
  static propTypes = {
    tabs: PropTypes.arrayOf(PropTypes.shape({
      href: PropTypes.string.isRequired,
      text: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
      ]),
      className: PropTypes.string,
      isIndex: PropTypes.bool,
      isExternalLink: PropTypes.bool
    })).isRequired,
    router: PropTypes.object.isRequired,
    centerDefault: PropTypes.bool,
    className: PropTypes.string,
    activeTabClassName: PropTypes.string
  }

  constructor(...args) {
    super(...args);
    this.removeScrollEvents = () => {};
    this.removeSetupEvents = () => {};
    this.scrollPosition = 0;
    this.maxTranslate = 0;

    // also defined elsewhere
    // this.minTranslate;
    // this.domElement
    // this.tabList
    // this.baseClass
    // this.leftEnd
    // this.rightEnd
    // this.supportsCssPointerEvents
  }

  componentDidMount() {
    this.supportsCssPointerEvents = (document.documentElement.style.pointerEvents === '');
    this.scrollTabIntoView(!!this.props.centerDefault);
    this.addScrollEvents();
    this.addSetupEvents();
  }

  componentWillUnmount() {
    this.removeScrollEvents();
    this.removeSetupEvents();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.tabs, this.props.tabs)) {
      this.scrollTabIntoView(false); // never try to center on update, only on initial mount
    }
  }

  getDomElements(el) {
    if (el) {
      this.domElement = el;
      this.tabList = el.querySelector("ul");
      this.baseClass = el.className;
      this.leftEnd = el.querySelector(`.${el.className}-left-end`);
      this.rightEnd = el.querySelector(`.${el.className}-right-end`);
    }
  }

  moveTabs(scrollBy) {
    return this.positionTabs(this.scrollPosition + scrollBy)
  }

  positionTabs(scrollTo) {
    this.minTranslate = this.tabList.clientWidth - this.tabList.scrollWidth;
    this.scrollPosition = Math.max(this.minTranslate, Math.min(this.maxTranslate, scrollTo));
    this.tabList.style.transform = `translate(${this.scrollPosition}px)`;
    this.setArrows();

    return (this.scrollPosition == scrollTo); // successfully moved to the requested position
  }

  setArrows() {
    this.minTranslate = this.tabList.clientWidth - this.tabList.scrollWidth;
    const canGoLeft = this.supportsCssPointerEvents && (this.scrollPosition < this.maxTranslate);
    const canGoRight = this.supportsCssPointerEvents && (this.scrollPosition > this.minTranslate);

    this.leftEnd.style.display = (canGoLeft ? "block" : "none");
    this.rightEnd.style.display = (canGoRight ? "block" : "none");
  }

  scrollTabIntoView(tryToCentre) {
    const activeLink = this.domElement.querySelector(".tab-selected");

    if (activeLink) {
      const activeTab = activeLink.parentElement;
      const domDimensions = this.domElement.getBoundingClientRect();
      const activeTabDimensions = activeTab.getBoundingClientRect();
      const offLeft = activeTabDimensions.left < domDimensions.left;
      const offRight = activeTabDimensions.right > domDimensions.right;

      if (tryToCentre) {
        this.moveTabs((domDimensions.left + domDimensions.right - activeTabDimensions.left - activeTabDimensions.right) / 2);
      }
      else if (offLeft) {
        this.moveTabs(domDimensions.left + arrowWidth - activeTabDimensions.left);
      }
      else if (offRight) {
        this.moveTabs(domDimensions.right - arrowWidth - activeTabDimensions.right);
      }
      else {
        this.setArrows();
      }
    }
  }

  addSetupEvents() {
    const onResize = () => this.moveTabs(0); // using moveTabs ensures the position limits are respected

    const eventList = [
      {
        target: window,
        event: "resize",
        handler: onResize
      }
    ];

    const scrollTimer = new ScrollTimer(this);
    const onMouseUp = () => {
      scrollTimer.stop();
      document.removeEventListener("mouseup", onMouseUp);
    };

    [this.leftEnd, this.rightEnd].forEach((end, i) => {
      const px = 1 - 2 * i; // +1 or -1
      const blockZone = new BlockZone(1000);

      /*
      Events are fired in these orders for the different user actions
        Click:
          mousedown (user) -> mouseup (user) -> click (auto: 1ms)
        Mouse Hold:
          mousedown (user) -> {{hold}} -> mouseup (user) -> click (auto: 1ms)
        Tap (touch release within 680ms):
          touchstart (user) -> touchend (user) -> mousedown (auto: usually 1ms, but 300ms observed) -> mouseup (1auto: ms) -> click (auto: 1ms)
        Touch Hold (touch release after 680ms):
          touchstart (user) -> mousedown (auto: 680ms observed) -> {{hold}} -> touchend (user)
      */
      eventList.push(
        {
          target: end,
          event: "touchstart",
          handler: () => {
            scrollTimer.start(px);
            blockZone.start();
          }
        },
        {
          target: end,
          event: "touchend",
          handler: () => {
            scrollTimer.stop();
          }
        },
        {
          target: end,
          event: "mousedown",
          handler: () => {
            if (blockZone.clear && scrollTimer.idle) {
              scrollTimer.start(px);
              document.addEventListener("mouseup", onMouseUp, false);
            }
          }
        }
      );
    });

    eventList.forEach(({target, event, handler}) => {
      if (target.addEventListener) {
        target.addEventListener(event, handler, false);
      }
    });
    onResize();

    this.removeSetupEvents = () => {
      eventList.forEach(({target, event, handler}) => target.removeEventListener(event, handler));
      document.removeEventListener("mouseup", onMouseUp);
    };
  }

  addScrollEvents() {
    let mask = document.createElement("div");
    mask.className = "mask"
    this.domElement.appendChild(mask);

    let startPointerX;
    let startTranslate = 0;

    const setStartPositions = eventX => {
      startPointerX = eventX;
      startTranslate = this.scrollPosition;
    }

    const movedEnough = eventX => {
      const movedBy = eventX - startPointerX;
      return (Math.abs(movedBy) > MOVE_MINIMUM);
    }

    const doDrag = eventX => {
      const moveTo = eventX - startPointerX + startTranslate;
      this.positionTabs(moveTo);
    }

    const prepareMouseMove = e => {
      setStartPositions(e.screenX);

      document.addEventListener("mousemove", startMouseMove, false);
      document.addEventListener("mouseup", endMouseMove, false);
    };

    const startMouseMove = e => {
       if (movedEnough(e.screenX)) {
        mask.style.display = "block";
        mask.addEventListener("mousemove", doMouseMove, false);
        document.removeEventListener("mousemove", startMouseMove);
      }
    };

    const doMouseMove = e => {
      doDrag(e.screenX);
    };

    const endMouseMove = () => {
      this.removeScrollEvents();
      mask.style.display = "none";
    }

    this.tabList.addEventListener("mousedown", prepareMouseMove, false);

    const prepareTouchMove = e => {
      setStartPositions(e.touches[0].screenX);
      this.tabList.addEventListener("touchmove", startTouchMove, false);
    };

    const startTouchMove = e => {
      if (movedEnough(e.touches[0].screenX)) {
        this.tabList.removeEventListener("touchmove", startTouchMove);
        this.tabList.addEventListener("touchmove", doTouchMove, false);
      }
    };

    const doTouchMove = e => {
      doDrag(e.touches[0].screenX);
    };

    const endTouchMove = () => {
      this.removeScrollEvents();
    }

    this.tabList.addEventListener("touchstart", prepareTouchMove, false);
    this.tabList.addEventListener("touchend", endTouchMove, false);

    // reassign
    this.removeScrollEvents = () => {
      document.removeEventListener("mousemove", startMouseMove);
      document.removeEventListener("mouseup", endMouseMove);

      this.tabList.removeEventListener("touchmove", startTouchMove);
      this.tabList.removeEventListener("touchmove", doTouchMove);
    };
  }



  render() {
    const {tabs, className = "tabs", activeTabClassName = "tab-selected" } = this.props;

    return (
      <nav className={className} ref={el => this.getDomElements(el)}>
        <ul>
          {tabs.map(tab => {
            const {
              href,
              isExternal = false,
              text,
              className
            } = tab;


            return <li key={tab.href}>
              {isExternal ?
                <a href={href} target="_blank">{text}</a> :
                <Link to={preventLinkScroll(href)} className={activeClassName(this.props.router, tab, activeTabClassName, className)}>{text}</Link>
              }
            </li>;
          })
        }
        </ul>
        <div className={`${className}-left-end`}><CaretIcon type="line" direction="left"/></div>
        <div className={`${className}-right-end`}><CaretIcon type="line" direction="right"/></div>
      </nav>
    );
  }
}

/*
Block period
  clear initially
  blocked for 1 second after being started
*/
class BlockZone {
  constructor(t) {
    this._time = t;
    this._blocked = false;
    this._timeoutId = 0;
  }

  get clear() {
    return !this._blocked;
  }

  start() {
    window.clearTimeout(this._timeoutId);
    this._blocked = true;

    this._timeoutId = window.setTimeout(() => {
      this._blocked = false;
    }, this._time);
  }
}

/*
Timer - one move after 100ms
      - wait 500ms
      - then animate
      - move checks if theres is anywhere to move to before advancing to the next frame

  Timer is "running" immediately
  Timer is "activated" one the 100ms first move has happened

  If a started timer is cancelled before being activated, do a block move (this is how click / tap will work)
*/
class ScrollTimer {
  constructor(tabset) {
    this._tabset = tabset;
    this._timeoutId = 0;
    this._started = false;
    this._activated = false;
  }

  get idle() {
    return !this._started;
  }

  moveTabs(by) {
    return this._tabset.moveTabs(by * this._px);
  }

  stop() {
    window.clearTimeout(this._timeoutId);

    if (this._started && !this._activated) {
      this.moveTabs(60);
    }

    this._started = false;
    this._activated = false;
  }

  start(px) {
    const initialDelay = 200;
    const holdPause = 500;
    const frameDelay = 1000 / 20; // 20 is frame rate

    this._px = px;
    this.stop();
    this._started = true;

    let frameCount = 0;

    const _frame = () => {
      window.clearTimeout(this._timeoutId);
      const msGap = [initialDelay, holdPause][frameCount++] || frameDelay;

      this._timeoutId = window.setTimeout(() => {
        this._activated = true;

        if (this.moveTabs(10)) {
          _frame();
        }
        else {
          this.stop();
        }
      }, msGap);
    };

    _frame();
  }
}



// WEBPACK FOOTER //
// ./modules/Common/components/Tabs.js